import React, { useCallback } from "react";
import { Button, Card, Flex, Label } from "@jsq/ds/core";
import { Form, FormTextInput, FormDatePicker, SubmitButton } from "@jsq/ds/forms";
import { Column } from "@jsq/ds/layouts";
import { AccountingPayableObjectTypeEnum } from "#graphql/supergraphEnums";
import {
  filterAvailableDate,
  getNextSwiftProcessingDateInLocal,
} from "../../../DistributionWorkflow/StepComponents/PaymentDetailsStep/utils";
import {
  buildTransactionUrl,
  payableObjectTypeToObjectType,
} from "../../../shared/transactionUrl";
import { PaymentBatchActionTypes } from "../../Context/constants";
import { usePaymentBatchDataContext, usePaymentBatchDispatch } from "../../Context/context";
import { PaymentBatchWorkflowStepProps } from "../../types";
import { getTransactionMoneyAmounts } from "../utils";
import { MakerNoteTextarea } from "./MakerNoteTextarea";
import { PayingCard } from "./PayingCard";
import {
  InitialValues,
  PaymentDetailsInputObject,
  PaymentDetailsStepInputFields,
} from "./schema";
import { datePickerContainer, label, paymentDescriptionContainer } from "./styles_vanilla.css";
import { TransactionDetailsCard } from "./TransactionDetailsCard";

const PaymentDetailsStep: React.FC<PaymentBatchWorkflowStepProps> = ({
  onBack,
  onContinue,
}: PaymentBatchWorkflowStepProps) => {
  const {
    batchData,
    batchId,
    batchObjectType,
    clientName,
    investmentEntityName,
    paymentDescription,
    scheduledPaymentProcessDate,
    makerNote,
    selectedPositions,
    numberOfAvailablePositions,
  } = usePaymentBatchDataContext();
  const dispatch = usePaymentBatchDispatch();

  const isBatchOf1 = selectedPositions.length === 1;
  const isBatchOf1Unpayable = isBatchOf1 && !selectedPositions[0].isSelectable;
  const positionsExcluded = numberOfAvailablePositions > selectedPositions.length;

  const nextAvailableProcessingDate = getNextSwiftProcessingDateInLocal();
  const onSubmit = useCallback(
    (values: InitialValues) => {
      dispatch({
        type: PaymentBatchActionTypes.SET_PAYMENT_DETAILS,
        payload: {
          scheduledPaymentProcessDate: values.scheduledPaymentProcessDate || undefined,
          paymentDescription: values.paymentDescription,
          makerNote: values.makerNote?.trim(),
        },
      });
      onContinue();
    },
    [onContinue, dispatch],
  );

  const paymentDetailsInitialValues: InitialValues = {
    paymentDescription: paymentDescription ?? "",
    scheduledPaymentProcessDate: scheduledPaymentProcessDate,
    makerNote: makerNote ?? "",
  };

  return (
    <Column size={"md"}>
      {batchData &&
        (() => {
          const {
            totalAmountPayable,
            amountMarkedPaid,
            amountPaidViaAutomatedPayments,
            amountInCurrentBatch,
          } = getTransactionMoneyAmounts(batchData, selectedPositions);
          const hostName = batchData.arena?.jsqHostname ?? null;
          const paymentObjectType = payableObjectTypeToObjectType(
            batchObjectType as AccountingPayableObjectTypeEnum,
          );
          const transactionUrl =
            hostName && batchId && paymentObjectType
              ? buildTransactionUrl(paymentObjectType, batchId, hostName)
              : null;
          return (
            <TransactionDetailsCard
              clientName={clientName}
              investmentEntityName={investmentEntityName}
              label={batchData.label}
              subtitleLabel={batchData.subtitleLabel}
              description={batchData.description}
              transactionUrl={transactionUrl}
              effectiveDate={batchData.date}
              requestedPaymentDate={batchData.requestedPaymentDate}
              totalAmountPayable={totalAmountPayable}
              amountMarkedPaid={amountMarkedPaid}
              amountPaidViaAutomatedPayments={amountPaidViaAutomatedPayments}
              amountInCurrentBatch={amountInCurrentBatch}
            />
          );
        })()}

      <Card title="Payment details">
        {/* PAYING: sub-section — batch of 1 only */}
        {isBatchOf1 && <PayingCard position={selectedPositions[0]} />}

        <Form
          onSubmit={onSubmit}
          submitOnlyChangedValues={false}
          initialValues={paymentDetailsInitialValues}
          validationSchema={PaymentDetailsInputObject(positionsExcluded)}
        >
          <Flex flexDirection="column">
            <div className={datePickerContainer}>
              <Label className={label}>Payment date</Label>
              <FormDatePicker
                name={PaymentDetailsStepInputFields.ScheduledPaymentProcessDate}
                hasIcon
                isFull={false}
                dateFormat="MM/dd/yyyy"
                placeholderText="Select date"
                filterDate={(date: Date) =>
                  filterAvailableDate(date, nextAvailableProcessingDate)
                }
                openToDate={nextAvailableProcessingDate.toJSDate()}
              />
            </div>
            <FormTextInput
              className={paymentDescriptionContainer}
              label="Payment description for recipients"
              name={PaymentDetailsStepInputFields.PaymentDescription}
              helpText={"35 characters max"}
            />
            <MakerNoteTextarea />
            <Flex flexDirection="row-reverse" gap={10}>
              <SubmitButton data-testid="continue-button" disabled={isBatchOf1Unpayable}>
                Continue
              </SubmitButton>
              <Button onClick={onBack} variant={"neutral"} data-testid="back-button">
                Back
              </Button>
            </Flex>
          </Flex>
        </Form>
      </Card>
    </Column>
  );
};

export default PaymentDetailsStep;
